import React from "react";
import { Link, Typography } from "@mui/material";
import LegalDocumentLayout, { LegalSection } from "./LegalDocumentLayout";

const contactEmail = "arjo@stanford.edu";

const sections: LegalSection[] = [
  {
    id: "agreement",
    title: "Agreement and eligibility",
    content: (
      <>
        <Typography variant="body1" paragraph>
          These Terms of Service govern your access to and use of StudySync,
          including the StudySync website, mobile application, researcher
          dashboard, participant enrollment flow, support tools, and related
          services that link to these Terms.
        </Typography>
        <Typography variant="body1" paragraph>
          By accessing or using StudySync, you agree to these Terms and to any
          study-specific terms, consent forms, researcher agreements, or
          institutional requirements that apply to your role. If you do not
          agree, do not use StudySync.
        </Typography>
        <Typography variant="body1" paragraph>
          StudySync is intended for users who are at least 18 years old unless a
          specific study permits minors with legally required parent or guardian
          consent and institutional approval.
        </Typography>
      </>
    ),
  },
  {
    id: "services",
    title: "StudySync services",
    content: (
      <>
        <Typography variant="body1" paragraph>
          StudySync provides software tools for approved research workflows,
          including study enrollment, participant verification, Apple
          Health/HealthKit and Fitbit data connection, research dashboards,
          adherence monitoring, support workflows, and data exports for study
          teams.
        </Typography>
        <Typography variant="body1" paragraph>
          StudySync is not an emergency service, medical device, diagnosis tool,
          clinical decision support system, or substitute for professional
          medical advice. If you may be experiencing a medical emergency, call
          911 or seek emergency medical help immediately.
        </Typography>
      </>
    ),
  },
  {
    id: "research",
    title: "Research participation and consent",
    content: (
      <>
        <Typography variant="body1" paragraph>
          Participation in any study is governed by the applicable consent
          materials, IRB-approved protocol, researcher instructions, and
          institutional policies. You should read those materials carefully
          before enrolling or authorizing data collection.
        </Typography>
        <Typography component="ul">
          <li>
            Participants are responsible for providing accurate enrollment and
            contact information and for following study instructions.
          </li>
          <li>
            Withdrawal from a study must follow the process described in the
            study materials or provided by the study team.
          </li>
          <li>
            Revoking Apple Health, Fitbit, or other device permissions may stop
            future collection but may not delete data already collected, which
            may be retained as allowed by the study consent, law, institutional
            policy, or research integrity requirements.
          </li>
        </Typography>
      </>
    ),
  },
  {
    id: "accounts",
    title: "Accounts and security",
    content: (
      <>
        <Typography variant="body1" paragraph>
          You are responsible for the accuracy of information you provide, the
          security of your account credentials, and activity that occurs through
          your account. Do not share your credentials or allow unauthorized
          access to StudySync.
        </Typography>
        <Typography variant="body1" paragraph>
          Notify us promptly if you believe your account, study access, or
          participant data may have been compromised. We may suspend or restrict
          access if needed to protect users, studies, data, or the platform.
        </Typography>
      </>
    ),
  },
  {
    id: "integrations",
    title: "Device integrations and permissions",
    content: (
      <>
        <Typography variant="body1" paragraph>
          StudySync may receive information from Apple Health/HealthKit, Fitbit,
          or other supported integrations only when authorized through the
          relevant app, device, account, or study workflow. You can revoke those
          permissions through the relevant service settings.
        </Typography>
        <Typography variant="body1" paragraph>
          Connected device data can be delayed, incomplete, inaccurate, or
          unavailable for reasons outside StudySync's control, including device
          settings, battery state, connectivity, third-party service outages, or
          changes to third-party APIs.
        </Typography>
      </>
    ),
  },
  {
    id: "researcher-responsibilities",
    title: "Researcher responsibilities",
    content: (
      <>
        <Typography variant="body1" paragraph>
          Researchers and study administrators are responsible for using
          StudySync only for approved and lawful research purposes.
        </Typography>
        <Typography component="ul">
          <li>Obtain and maintain required IRB, ethics, institutional, and
            participant approvals before collecting or using study data.</li>
          <li>Configure studies accurately, including data sources, participant
            instructions, collaborators, and export settings.</li>
          <li>Limit access to authorized study personnel and remove access when
            it is no longer needed.</li>
          <li>Protect exported data and handle it according to study protocols,
            institutional policies, data use agreements, and applicable law.</li>
          <li>Do not use StudySync as the sole mechanism for participant safety
            monitoring, clinical decision-making, or urgent care unless the
            study has separately approved and validated that workflow.</li>
        </Typography>
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "Acceptable use",
    content: (
      <>
        <Typography variant="body1" paragraph>
          You may use StudySync only for lawful, authorized purposes consistent
          with these Terms, the Privacy Policy, and applicable study materials.
        </Typography>
        <Typography component="ul">
          <li>Do not access data, accounts, studies, or systems without
            authorization.</li>
          <li>Do not interfere with platform security, availability, rate
            limits, or monitoring.</li>
          <li>Do not upload malware, attempt to bypass authentication, scrape
            the service, or reverse engineer non-public components.</li>
          <li>Do not re-identify de-identified or aggregated data unless
            expressly authorized by the study protocol and law.</li>
          <li>Do not use StudySync data for advertising, marketing, sale, or
            unrelated data mining.</li>
        </Typography>
      </>
    ),
  },
  {
    id: "privacy",
    title: "Privacy and data",
    content: (
      <>
        <Typography variant="body1" paragraph>
          The StudySync{" "}
          <Link href="/privacy-policy" underline="hover">
            Privacy Policy
          </Link>{" "}
          explains how information is collected, used, shared, retained, and
          protected. By using StudySync, you acknowledge that the Privacy Policy
          applies to your use of the services.
        </Typography>
        <Typography variant="body1" paragraph>
          Study data may also be subject to study-specific consent forms,
          institutional policies, data use agreements, and laws that apply to
          the study team, research institution, or participant.
        </Typography>
      </>
    ),
  },
  {
    id: "intellectual-property",
    title: "Intellectual property",
    content: (
      <>
        <Typography variant="body1" paragraph>
          StudySync, including its software, design, text, graphics, workflows,
          and other platform materials, is owned by StudySync or its licensors
          and is protected by intellectual property laws. Subject to these
          Terms, you may use StudySync only for its intended research platform
          purposes.
        </Typography>
        <Typography variant="body1" paragraph>
          You retain rights to information you submit, subject to the rights and
          permissions needed for StudySync and approved study teams to operate
          the services, support research, comply with applicable obligations,
          and enforce these Terms.
        </Typography>
      </>
    ),
  },
  {
    id: "third-party-services",
    title: "Third-party services",
    content: (
      <>
        <Typography variant="body1" paragraph>
          StudySync may interoperate with third-party services such as Apple
          Health/HealthKit, Fitbit, Firebase, Google Cloud, analytics providers,
          email or messaging providers, and cloud infrastructure providers.
          Those services may be governed by their own terms, privacy policies,
          account settings, and availability limitations.
        </Typography>
      </>
    ),
  },
  {
    id: "availability",
    title: "Availability, changes, and termination",
    content: (
      <>
        <Typography variant="body1" paragraph>
          We may update, suspend, restrict, or discontinue StudySync features at
          any time, including to improve the platform, address security issues,
          comply with law, or support study requirements.
        </Typography>
        <Typography variant="body1" paragraph>
          We may suspend or terminate access if you violate these Terms, misuse
          the platform, create security or compliance risk, or if your study or
          institution no longer authorizes access.
        </Typography>
      </>
    ),
  },
  {
    id: "disclaimers",
    title: "Disclaimers",
    content: (
      <>
        <Typography variant="body1" paragraph>
          StudySync is provided on an "as is" and "as available" basis. To the
          fullest extent permitted by law, we disclaim warranties of
          merchantability, fitness for a particular purpose, non-infringement,
          accuracy, availability, and error-free operation.
        </Typography>
        <Typography variant="body1" paragraph>
          StudySync does not guarantee that connected device data, exports,
          alerts, dashboards, or analytics will be complete, accurate, timely,
          clinically meaningful, or suitable for any particular research,
          operational, medical, or regulatory purpose.
        </Typography>
      </>
    ),
  },
  {
    id: "liability",
    title: "Limitation of liability and indemnity",
    content: (
      <>
        <Typography variant="body1" paragraph>
          To the fullest extent permitted by law, StudySync and its contributors
          will not be liable for indirect, incidental, consequential, special,
          exemplary, punitive, or similar damages, or for loss of data, profits,
          goodwill, or business opportunities arising from or related to your use
          of StudySync.
        </Typography>
        <Typography variant="body1" paragraph>
          If you use StudySync on behalf of a research team, institution, or
          organization, you agree that the organization is responsible for your
          authorized use and for compliance with applicable study obligations,
          approvals, policies, and laws.
        </Typography>
      </>
    ),
  },
  {
    id: "governing-law",
    title: "Governing law",
    content: (
      <>
        <Typography variant="body1" paragraph>
          These Terms are governed by the laws of the State of California,
          without regard to conflict-of-law principles, unless a separate
          written agreement or study-specific document states otherwise.
        </Typography>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    content: (
      <>
        <Typography variant="body1" paragraph>
          Questions about these Terms can be sent to{" "}
          <Link href={`mailto:${contactEmail}`}>{contactEmail}</Link>. Study
          participants should also use the study team contact information listed
          in their consent materials for study-specific questions.
        </Typography>
      </>
    ),
  },
];

const TermsOfService: React.FC = () => {
  return (
    <LegalDocumentLayout
      title="Terms of Service"
      lastUpdated="May 25, 2026"
      relatedLink={{ label: "Privacy Policy", to: "/privacy-policy" }}
      intro={
        <>
          <Typography variant="body1" paragraph>
            These Terms explain the rules for using StudySync as a participant,
            researcher, collaborator, administrator, or visitor.
          </Typography>
          <Typography variant="body1" paragraph>
            They are written for a research and health-data platform, so they
            include study consent, device permissions, account security,
            acceptable use, privacy, and no-medical-advice terms.
          </Typography>
        </>
      }
      sections={sections}
    />
  );
};

export default TermsOfService;
